import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, map } from 'rxjs';

import { AccessMap, Channel, ChannelReadResponse, ChannelResponse, ChannelType, ChannelTyper, ChannelTyperStatus, ChannelTypers, CreateChannelRequest, CreateGroupRequest, UpdateGroupRequest } from '@weavix/models/src/channel/channel';
import { AlertService } from './alert.service';
import { HttpService } from './http.service';
import { ProfileService } from './profile.service';
import { PubSubService } from './pub-sub.service';

import { AccountService } from 'weavix-shared/services/account.service';

import { Person } from '../models/person.model';
import { Topic } from '@weavix/models/src/topic/topic';
import { RADIO_WINDOW_TARGET } from '../utils/utils';
import { environment } from '../../environments/environment';

import { ChannelNotifications } from '@weavix/models/src/channel/channel-notifications';
import { ChannelMessageQuery, ChannelMessageResponse, Communication, CreateMessageRequest, MessageAttachment, MessageAttachmentType, URLLinkPreview } from '@weavix/models/src/channel/channel-message';
import { LinkPreviewRequest } from '../models/channels.model';
import { ChannelMessage } from '../../models-mobx/channels-store/channel-message';
import { channelColors } from '@weavix/domain/src/utils/colors';

export function isAttachmentGif(attachment: MessageAttachment | PendingAttachment): boolean {
    return '.gif' === attachment?.file?.name.substr(attachment?.file?.name.length - 4);
}

export interface PendingAttachment extends MessageAttachment {
    raw?: File;
}

@Injectable({
    providedIn: 'root',
})
export class ChannelService {

    constructor(
        private readonly httpService: HttpService,
        private readonly accountService: AccountService,
        private readonly profileService: ProfileService,
        private readonly pubSubService: PubSubService,
        private readonly alertsService: AlertService,
    ) {
    }

    get visibility() { return this.isVisible; }
    set setVisiblity(isVisible: boolean) { this.isVisible = isVisible; }
    typers: { [id: string]: ChannelTyper[] };

    selectChannel = new BehaviorSubject<{ id: string }>({ id: '' });
    hideChannelsList = new BehaviorSubject<boolean>(false);
    channelIdNameMap: { [key: string]: string } = {};
    isAttachmentGif = isAttachmentGif;

    private readonly url: string = '/core/channels';
    static getUrl = (id?: string) => (id ? `/core/channels/${id}` : '/core/channels');

    private isVisible: boolean = false;
    private readonly accountUrl = (channel: Channel) => channel.accountId ? `/a/${channel.accountId}/core/channels` : '/core/channels';

    async buildChannelMap(component) {
        const lists = await this.getChannels(component);
        this.channelIdNameMap = {};
        lists.forEach(l => this.channelIdNameMap[l.id] = l.name);
    }

    toggleVisibility(): void {
        this.setVisiblity = !this.isVisible;
    }

    getChannels(component, facilityId?: string, tags?) {
        return this.httpService.get<ChannelResponse[]>(component, this.url, { facilityId, tags }, null, null);
    }

    getAllChannels(component) {
        return this.httpService.get<ChannelResponse[]>(component, '/core/me/channels', undefined, null, null);
    }

    getSubscribedPeople(component, channel: Channel) {
        return this.httpService.get<Person[]>(component, `${this.accountUrl(channel)}/${channel.id}/subscribed`, undefined, null, (person: Person) => person.fullName);
    }

    getChannel(component, id: string) {
        return this.httpService.get<ChannelResponse>(component, `${this.url}/${id}`, null);
    }

    /** ADC */
    addChannel(component, channel: CreateChannelRequest) {
        return this.httpService.post<ChannelResponse>(component, `${this.url}`, channel);
    }

    /** UDC */
    addGroup(component, channel: CreateGroupRequest) {
        return this.httpService.post<ChannelResponse>(component, `${this.url}/groups`, channel);
    }

    /** ADC or UDC */
    updateChannel(component, channel: Channel, update?: Partial<CreateChannelRequest | UpdateGroupRequest>) {
        channel = this.cleanTags(channel);
        if (channel.type === ChannelType.Channel) {
            const accountPrefix = channel.accountId ? `/a/${channel.accountId}` : '';
            return this.httpService.put<Channel>(component, `${accountPrefix}${this.url}/${channel.id}`, update ?? channel);
        } else if (channel.type === ChannelType.People) {
            return this.httpService.put<Channel>(component, `${this.url}/groups/${channel.id}`, update ?? channel);
        }
    }

    private cleanTags(channel: Channel) {
        if (channel.tags) {
            channel.tags = channel.tags.filter(x => x !== undefined);
        }
        return channel;
    }

    updateChannelName(component, channel: Channel, name: string) {
        return this.httpService.put<Channel>(component, `${this.accountUrl(channel)}/${channel.type === ChannelType.People ? 'groups/' : ''}${channel.id}`, { name: name || '' });
    }

    deleteChannel(component, id: string) {
        return this.httpService.delete<ChannelResponse>(component, `${this.url}/${id}`, null);
    }

    async markChannelAsRead(component, channel: ChannelResponse) {
        // self correction in case channel sequence is lower than the sequence of the latest channel message
        if (channel.readSequence > channel.sequence) {
            await this.httpService.put<ChannelReadResponse>(component, `${this.accountUrl(channel)}/${channel.id}/self-correct`, {
                readSequence: channel.readSequence,
            });
        }

        return await this.httpService.put<ChannelReadResponse>(component, `${this.accountUrl(channel)}/${channel.id}/preferences`, {
            read: true,
        });
    }

    markChannelAsSnoozed(component, channel: Channel) {
        return this.httpService.put<ChannelReadResponse>(component, `${this.accountUrl(channel)}/${channel.id}/preferences`, { isSnoozed: true });
    }

    setTyping(component, channel: Channel, status: ChannelTyperStatus) {
        return this.httpService.put<ChannelReadResponse>(component, `${this.accountUrl(channel)}/${channel.id}/typing`, { status });
    }

    updateNotificationType(component, channel: Channel, notification: ChannelNotifications) {
        return this.httpService.put(component, `${this.accountUrl(channel)}/${channel.id}/preferences`, { notification });
    }

    pageChannel(component, channel: Channel) {
        return this.httpService.put(component, `${this.accountUrl(channel)}/${channel.id}/page`, {});
    }

    async getChannelMessages(component, channel: Channel, query?: ChannelMessageQuery) {
        return await this.httpService.get<ChannelMessageResponse[]>(component, `${this.accountUrl(channel)}/${channel.id}/messages`, query, null, null);
    }

    addChannelMessage(component, channelMessage: CreateMessageRequest, channel: Channel) {
        return this.httpService.post<ChannelMessageResponse>(component, `${this.accountUrl(channel)}/${channel.id}/message`, channelMessage);
    }

    editChannelMessage(component, channelMessage: ChannelMessage, channelMessageId: string, channel: Channel) {
        return this.httpService.post<ChannelMessageResponse>(component, `${this.accountUrl(channel)}/${channel.id}/messages/${channelMessageId}`, channelMessage);
    }

    async getCommunications(component, facilityId: string, from: Date, to?: Date) {
        return this.httpService.get<Communication[]>(component, '/core/channels/communications', { facilityId, from, to });
    }

    async subscribeCommunications(component, facilityId: string) {
        const account = await this.accountService.getAccount();
        return this.pubSubService.subscribe<Communication>(component, Topic.AccountFacilityCommunication, [account.id, facilityId]);
    }

    async newAlertSubscription(component): Promise<Observable<ChannelMessageResponse>> {
        const userProfile = await this.profileService.getUserProfile(component);
        if (userProfile) {
            return this.pubSubService.subscribe<ChannelMessageResponse>(component, Topic.UserChannelMessageCreated, [userProfile.id])
                    .pipe(map(x => x.payload));
        }
        return of(null);
    }

    async subscribeTypers(component, channelId: string): Promise<Observable<ChannelTyper[]>> {
        const userProfile = await this.profileService.getUserProfile(component);
        return this.pubSubService.subscribe<ChannelTypers>(component, Topic.UserChannelTypers, [userProfile.id, channelId], true)
            .pipe(map(x => {
                return x.payload.typers;
            }));
    }

    async uploadMessageAttachments(component, channel: Channel, type: MessageAttachmentType, formData) {
        try {
            return await this.httpService.upload<MessageAttachment>(component, `${this.accountUrl(channel)}/${channel.id}/message/attachment/${type}`, formData);
        } catch (e) {
            const reason: string = e.error?.details?.reason;
            switch (reason) {
                case ('file_too_large') :
                    this.alertsService.sendError({ error: e, messageKey: 'ERRORS.FILE.TOO-LARGE' });
                    break;
                case ('invalid_attachment_type') :
                    this.alertsService.sendError({ error: e, messageKey: 'ERRORS.CHANNEL.INVALID_ATTACHMENT_TYPE' });
                    break;
                case ('invalid_file_type') :
                    this.alertsService.sendError({ error: e, messageKey: 'ERRORS.CHANNEL.INVALID_FILE_TYPE' });
                    break;
                default :
                    this.alertsService.sendError({ error: e, messageKey: 'ERRORS.CHANNEL.ATTACHMENT-UPLOAD-FAILED' });
            }
            throw (e);
        }
    }

    async getLinkPreviewInfo(component, urlRequest: LinkPreviewRequest) {
        return this.httpService.post<URLLinkPreview>(component, `/link-preview`, urlRequest);
    }

    async downloadAttachment(uri) {
        this.httpService.attachmentDownload(uri);
    }

    async setMqttClientSelectedChannel(component, channelId: string) {
        const postBody = {
            clientId: this.pubSubService.clientId,
            channelId,
        };
        return this.httpService.post<URLLinkPreview>(component, `/teams/client-connection-channel`, postBody);
    }

    async openTabToRadio(component?: any, ...peopleIds: string[]) {
        try {
            let url = `${environment.radioUrl}/radio`;
            if (peopleIds?.length > 0) {
                if (component == null) throw new Error('component is required');
                const channel = await this.addGroup(component, { people: peopleIds });
                url += `?id=${channel.id}`;
            }
            window.open(url, RADIO_WINDOW_TARGET);
        } catch (e) {
            this.alertsService.sendError({ error: e, messageKey: 'ERRORS.CHANNEL.OPEN_TAB' });
        }
    }

    async getChannelAccess(component, channel: Channel) {
        return this.httpService.get<AccessMap>(component, `${this.accountUrl(channel)}/${channel.id}/access`);
    }

    async colors(component: any) {
        return channelColors;
    }
}
